import { FilePdfOutlined, LeftOutlined } from '@ant-design/icons';
import {
  type ArrearsFilter,
  BackBar,
  billsCount,
  daysCount,
  dirhams,
  filterArrears,
  monthYear,
  openReportPdf,
  present,
  totalDue,
} from '@horof/shared';
import { Alert, App, Button, Empty, Segmented, Skeleton, Tag } from 'antd';
import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from 'urql';
import { ArrearsDocument } from './reports-data';

/** Every member who owes, most overdue first (arqam's arrearsList). */
export function ArrearsPage() {
  const { message } = App.useApp();
  const [{ data, error }] = useQuery({
    query: ArrearsDocument,
    requestPolicy: 'cache-and-network',
  });
  const [filter, setFilter] = useState<ArrearsFilter>('all');
  const [opening, setOpening] = useState(false);

  const all = present(data?.arrearsList?.nodes);
  const rows = filterArrears(all, filter);

  async function openPdf() {
    setOpening(true);
    try {
      await openReportPdf('arrears');
    } catch {
      message.error('تعذّر فتح التقرير. أعد المحاولة.');
    } finally {
      setOpening(false);
    }
  }

  return (
    <div className="page">
      <BackBar to="/reports" label="التقارير" />
      <h1 className="member-title">المتأخرون عن الأداء</h1>
      {error && <Alert type="error" showIcon message="تعذّر تحميل القائمة" />}
      {!data && !error && <Skeleton active paragraph={{ rows: 8 }} />}

      {data && (
        <>
          <section className="card arrears-total">
            <div>
              <div className="field-hint">المجموع</div>
              <div className="total-number">{dirhams(totalDue(rows))}</div>
            </div>
            <div>
              <div className="field-hint">الأعضاء</div>
              <div className="total-number ltr-num">{rows.length}</div>
            </div>
          </section>
          <Segmented<ArrearsFilter>
            block
            size="large"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: `الكل (${all.length})` },
              {
                value: 'public',
                label: `اللائحة العامة (${filterArrears(all, 'public').length})`,
              },
              {
                value: 'unreachable',
                label: `بدون هاتف (${filterArrears(all, 'unreachable').length})`,
              },
            ]}
          />
          <Button
            size="large"
            icon={<FilePdfOutlined />}
            loading={opening}
            onClick={openPdf}
          >
            التقرير الكامل (PDF)
          </Button>
        </>
      )}
      {data && rows.length === 0 && <Empty description="لا أحد" />}

      <ul className="member-list">
        {rows.map((r) => (
          <li key={r.memberId}>
            <Link to={`/members/${r.memberId}`} className="member-row">
              <span className="member-main">
                <span className="member-name">{r.fullName}</span>
                <span className="member-sub">
                  {billsCount(r.billsCount)}
                  {r.oldestBillMonth && ` منذ ${monthYear(r.oldestBillMonth)}`}
                  {r.daysOverdue > 0 && ` · متأخر ${daysCount(r.daysOverdue)}`}
                </span>
                <span className="arrears-tags">
                  {r.publiclyListed && <Tag color="red">في اللائحة العامة</Tag>}
                  {(r.phoneStatus !== 'OK' || r.notifyChannel === 'NONE') && (
                    <Tag>بدون هاتف</Tag>
                  )}
                </span>
              </span>
              <span className="arrears-amount">{dirhams(r.amountDue)}</span>
              <LeftOutlined className="row-link-arrow" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
